import { createContext, useContext, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { getModule, MODULES, moduleStyle, type BulkModule, type ModuleKey } from '@/lib/modules';
import { cn } from '@/lib/utils';

const TILE_GRAY = 'var(--color-tile, #dde1e6)';

export function BulkMark({ size = 24, lit, className }: { size?: number; lit?: ModuleKey | 'all' | null; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" className={cn('shrink-0', className)} aria-hidden>
      {MODULES.map((m, i) => (
        <rect key={m.key} x={2 + (i % 3) * 15.3} y={2 + Math.floor(i / 3) * 15.3} width="13" height="13" rx="3.2" style={{ fill: lit === 'all' ? m.color : lit ? (m.key === lit ? m.color : TILE_GRAY) : 'currentColor' }} />
      ))}
    </svg>
  );
}

export function BulkLockup({ size = 28, lit, className }: { size?: number; lit?: ModuleKey | 'all' | null; className?: string }) {
  return (
    <span className={cn('inline-flex items-center text-ink', className)} style={{ gap: size * 0.32 }}>
      <BulkMark size={size * 0.86} lit={lit} />
      <span className="font-extrabold leading-none tracking-[-0.04em]" style={{ fontSize: size * 0.82 }}>
        Bulk
      </span>
    </span>
  );
}

const tileSizes = {
  xs: 'h-4 w-4 rounded-[4px] [&_svg]:h-2.5 [&_svg]:w-2.5',
  sm: 'h-6 w-6 rounded-[6px] [&_svg]:h-3.5 [&_svg]:w-3.5',
  md: 'h-8 w-8 rounded-[8px] [&_svg]:h-4 [&_svg]:w-4',
  lg: 'h-11 w-11 rounded-[11px] [&_svg]:h-5 [&_svg]:w-5',
  xl: 'h-14 w-14 rounded-[14px] [&_svg]:h-7 [&_svg]:w-7',
} as const;

export function ModuleTile({ module, icon, size = 'md', soft, className }: { module: ModuleKey | null; icon?: LucideIcon; size?: keyof typeof tileSizes; soft?: boolean; className?: string }) {
  const Icon = icon ?? (module ? getModule(module).icon : null);
  return (
    <span style={moduleStyle(module)} className={cn('inline-flex shrink-0 items-center justify-center transition-colors', soft ? 'bg-mod-soft text-mod-ink' : 'bg-mod text-white', tileSizes[size], className)} aria-hidden>
      {Icon ? <Icon strokeWidth={2.25} /> : null}
    </span>
  );
}

/** Where a page sits in its surface's menu: the group's name and the entry's own icon. */
export interface PageSection {
  label: string;
  icon: LucideIcon;
}

const ModuleContext = createContext<BulkModule | null>(null);
const SectionContext = createContext<PageSection | null>(null);

export function ModuleProvider({ module, section = null, children }: { module: ModuleKey | null; section?: PageSection | null; children: ReactNode }) {
  return (
    <ModuleContext.Provider value={module ? getModule(module) : null}>
      <SectionContext.Provider value={section}>{children}</SectionContext.Provider>
    </ModuleContext.Provider>
  );
}

export function useCurrentModule(): BulkModule | null {
  return useContext(ModuleContext);
}

export function useCurrentSection(): PageSection | null {
  return useContext(SectionContext);
}
